import { ResponseType } from 'axios'
import { createSelector } from 'reselect'
import { ExtendThreadIDHeader } from 'src/queries/requestConfig.models'
import { tokenStore } from 'src/queries/tokenStore'
import {
  selectCspJwtFromState,
  selectDefaultCspIssuerId,
  selectIsCspAuthenticatedFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import {
  selectAddressulatorHostFromState,
  selectAgentHostFromState,
  selectApiHostFromState,
  selectBrandId,
  selectLocalUserIdFromState,
  selectPrimaryDomain,
  selectProxulatorHostFromState,
} from 'src/redux/selectors/session.selectors'
import { selectVaultHostFromState } from 'src/redux/selectors/vault.selectors'

// Moved to the non-Redux request boundary; re-exported here for the legacy
// consumers of this module.
export { ExtendThreadIDHeader }

// LEGACY BOUNDARY: `getRequestConfig()` (src/queries/requestConfig.ts) no longer
// consumes this module — it builds configs from `tokenStore` + `requestContext`
// directly. These selectors remain only for the Redux thunk files scheduled for
// deletion by the RQ-migration pillar (plus the App.tsx Pusher effect, which
// depends on the memoized config identity). Both implementations must stay
// byte-identical until the thunks die; the shared oracle is
// src/queries/__tests__/requestConfig.parity.test.ts.
//
// Primary-session JWT bearer source (auth-redux-elimination slice 2 keystone): the
// transport Authorization header derives from the non-Redux `tokenStore`, not
// `state.auth.jwt`. Kept as a reselect input selector so the memoized header objects
// recompute exactly when the bearer changes (string identity) and preserve the
// `jwt === undefined` -> no-`Authorization` behaviour byte-for-byte. The interop
// mirror (tokenStore.interop.ts) keeps `tokenStore` equal to Redux, so this is
// byte-identical to the former Redux jwt-selector read. The CSP bearer
// (`selectCspAuthedJsonRequestHeaders`, below) intentionally stays on Redux here.
const selectPrimarySessionBearerFromStore = (): string | undefined =>
  tokenStore.getAccessToken()

const selectBaseRequestHeaders = createSelector(
  [
    selectBrandId,
    selectPrimaryDomain,
    selectIsCspAuthenticatedFromState,
    selectLocationFromState,
  ],
  (brandId, primaryDomain, isCspAuthenticated, location) => ({
    'X-Extend-Brand': brandId,
    'X-Extend-App-ID': primaryDomain,
    'X-Extend-Platform':
      isCspAuthenticated || location.pathname.includes('/csp/')
        ? 'mira'
        : 'web',
    'X-Extend-Platform-Version':
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
    // until org switcher is built out, hard code the org id into this header
    // 'X-Extend-Organization': '',
    Accept: 'application/vnd.paywithextend.v2021-03-12+json',
  }),
)

const selectUnauthedJsonRequestHeaders = createSelector(
  [selectBaseRequestHeaders],
  (baseRequestHeaders) => ({
    ...baseRequestHeaders,
    'Content-Type': 'application/json',
  }),
)

const selectUnauthedFormRequestHeaders = createSelector(
  [selectBaseRequestHeaders],
  (baseRequestHeaders) => ({
    ...baseRequestHeaders,
    'Content-Type': 'multipart/form-data',
  }),
)

const selectAuthedJsonRequestHeaders = createSelector(
  [
    selectUnauthedJsonRequestHeaders,
    selectPrimarySessionBearerFromStore,
    selectLocalUserIdFromState,
  ],
  (unauthedJsonRequestHeaders, jwt, localUserId) => {
    const headers =
      jwt === undefined
        ? unauthedJsonRequestHeaders
        : {
            ...unauthedJsonRequestHeaders,
            Authorization: `Bearer ${jwt}`,
          }
    return localUserId ? { ...headers, 'X-Local-User-Id': localUserId } : headers
  },
)

const selectAuthedFormRequestHeaders = createSelector(
  [
    selectUnauthedFormRequestHeaders,
    selectPrimarySessionBearerFromStore,
    selectLocalUserIdFromState,
  ],
  (unauthedFormRequestHeaders, jwt, localUserId) => {
    const headers =
      jwt === undefined
        ? unauthedFormRequestHeaders
        : {
            ...unauthedFormRequestHeaders,
            Authorization: `Bearer ${jwt}`,
          }
    return localUserId ? { ...headers, 'X-Local-User-Id': localUserId } : headers
  },
)

const selectCspAuthedJsonRequestHeaders = createSelector(
  [selectUnauthedJsonRequestHeaders, selectCspJwtFromState],
  (unauthedJsonRequestHeaders, cspJwt) =>
    cspJwt === undefined
      ? unauthedJsonRequestHeaders
      : {
          ...unauthedJsonRequestHeaders,
          Authorization: `Bearer ${cspJwt}`,
        },
)

const selectCspAuthedIssuerIdJsonRequestHeaders = createSelector(
  [selectCspAuthedJsonRequestHeaders, selectDefaultCspIssuerId],
  (cspAuthedJsonRequestHeadersw, defaultCspIssuerId) =>
    defaultCspIssuerId
      ? {
          ...cspAuthedJsonRequestHeadersw,
          'X-Issuer-Id': defaultCspIssuerId,
        }
      : {
          ...cspAuthedJsonRequestHeadersw,
        },
)

export const selectUnauthedJsonApiRequestConfig = createSelector(
  [selectUnauthedJsonRequestHeaders, selectApiHostFromState],
  (unauthedJsonRequestHeaders, apiHost) => ({
    headers: unauthedJsonRequestHeaders,
    baseURL: apiHost,
  }),
)

export const selectUnauthedJsonProxulatorRequestConfig = createSelector(
  [selectProxulatorHostFromState],
  (proxulatorHost) => ({
    baseURL: proxulatorHost,
  }),
)

export const selectAuthedJsonProxulatorRequestConfig = createSelector(
  [selectApiHostFromState, selectAuthedJsonRequestHeaders],
  (apiHost, authedJsonRequestHeaders) => ({
    headers: {
      ...authedJsonRequestHeaders,
      Accept: 'application/vnd.paywithextend.v2021-03-12+json',
    },
    baseURL: apiHost,
  }),
)

export const selectUnauthedJsonAddressulatorRequestConfig = createSelector(
  [selectAddressulatorHostFromState],
  (apiHost) => ({
    headers: {
      'Content-Type': 'application/json',
    },
    baseURL: apiHost,
  }),
)

export const selectAuthedJsonVaultRequestConfig = createSelector(
  [selectAuthedJsonRequestHeaders, selectVaultHostFromState],
  (authedJsonRequestHeaders, vaultHost) => ({
    headers: authedJsonRequestHeaders,
    baseURL: vaultHost,
  }),
)

export const selectAuthedJsonApiRequestConfig = createSelector(
  [selectAuthedJsonRequestHeaders, selectApiHostFromState],
  (authedJsonRequestHeaders, apiHost) => ({
    headers: authedJsonRequestHeaders,
    baseURL: apiHost,
  }),
)

export const selectAuthedJsonAgentRequestConfig = createSelector(
  [selectAuthedJsonRequestHeaders, selectAgentHostFromState],
  (authedJsonRequestHeaders, agentHost) => ({
    headers: {
      ...authedJsonRequestHeaders,
      [ExtendThreadIDHeader]: '',
    },
    baseURL: agentHost,
  }),
)

export const selectAuthedJsonApiRequestConfigPlusArrayBufferResponseType =
  createSelector(
    [selectAuthedJsonRequestHeaders, selectApiHostFromState],
    (authedJsonRequestHeaders, apiHost) => ({
      headers: authedJsonRequestHeaders,
      baseURL: apiHost,
      responseType: 'arraybuffer' as ResponseType,
    }),
  )

export const selectAuthedFormApiRequestConfig = createSelector(
  [selectAuthedFormRequestHeaders, selectApiHostFromState],
  (authedFormRequestHeaders, apiHost) => ({
    headers: authedFormRequestHeaders,
    baseURL: apiHost,
  }),
)

export const selectCspAuthedJsonApiRequestConfig = createSelector(
  [selectCspAuthedJsonRequestHeaders, selectApiHostFromState],
  (cspAuthedJsonRequestHeaders, apiHost) => ({
    headers: cspAuthedJsonRequestHeaders,
    baseURL: apiHost,
  }),
)

export const selectCspAuthedIssuerIdJsonApiRequestConfig = createSelector(
  [selectCspAuthedIssuerIdJsonRequestHeaders, selectApiHostFromState],
  (cspAuthedJsonRequestHeaders, apiHost) => ({
    headers: cspAuthedJsonRequestHeaders,
    baseURL: apiHost,
  }),
)
